/* eslint-disable react/display-name */
import React, { useRef } from 'react'
import { connect } from 'react-redux'
import { Col, Image, Row, Space } from 'antd'
import { formatAddress, formatDateTime, getGrnStatus, numberWithComma } from 'helpers/format'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { InfoItem } from 'Pages/Merchants/components'
import { PrimaryButton } from 'Components/button'
import ReactToPrint from 'react-to-print'
import { PrinterOutlined } from '@ant-design/icons'
import CnPDF from './CnPDF'
import { CreditNotesData, GrnData } from 'Store/grn/types'
import { StatusTag } from 'Components/utilities'
import { ImageFallback } from 'helpers/constanst'
import { TableListPage } from 'Components/common/Table'
import { PurchaseProduct } from 'Store/purchase/types'
import { appConfig } from 'config'
import ProductUomSystemInfo from 'Pages/Product/components/ProductUomSystemInfo'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  creditNotesData?: CreditNotesData
  detail?: GrnData
}

const CnDetail: React.FunctionComponent<Props> = props => {
  const { onCloseDrawer, visible, creditNotesData, detail } = props
  const printPDF = useRef(null as any)

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const renderTitle = () => {
    return (
      <Row>
        <Col span={24}>เลขที่ใบลดหนี้ : {creditNotesData?.credit_note_no}</Col>
      </Row>
    )
  }

  const renderStatus = (status: string) => {
    const statusResult = getGrnStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const renderGrnInfo = () => {
    return (
      <BoxWrapper>
        <Row gutter={8}>
          <Col md={12} xs={24}>
            <BoxTitle>รายละเอียดการรับเข้า</BoxTitle>
            {renderInfo('เลขที่ใบจ่ายหนี้', detail?.grn_no)}
            {renderInfo('วันที่รับเข้าคลัง', formatDateTime(detail?.recieved_date || ''))}
            {renderInfo('ผู้รับเข้า', detail?.username)}
            {renderInfo('สถานะ', renderStatus(detail?.status || ''))}
          </Col>
          {/* <Col md={12} xs={24}>
            {renderInfo('ผู้รับเข้า', detail?.username)}
            {renderInfo('สถานะ', renderStatus(detail?.status || ''))}
          </Col> */}
          <Col md={12} xs={24}>
            <BoxTitle>รายละเอียดการสั่งซื้อ</BoxTitle>
            <Row gutter={8}>
              <Col md={24} xs={24}>
                {renderInfo('เลขที่ใบสั่งซื้อ', detail?.purchasing?.purchasing_no)}
                {/* {renderInfo('บริษัท/Supplier', detail?.supplier?.name)}
                {renderInfo('ที่อยู่', detail?.supplier?.address)}
                {renderInfo('เบอร์โทรศัพท์', detail?.supplier?.mobile_no)} */}
                {renderInfo('วันที่สั่งซื้อ', formatDateTime(detail?.purchasing?.purchasing_date || ''))}
                {renderInfo('ผู้ออกใบสั่งซื้อ', detail?.purchasing?.purchasing_by)}
                {renderInfo('หมายเหตุ', detail?.purchasing?.remark)}
              </Col>
            </Row>
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderPurchaseInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดผู้จำหน่าย</BoxTitle>
        <Row gutter={8}>
          <Col md={24} xs={24}>
            {renderInfo('บริษัท/Supplier', detail?.supplier?.name)}
            {renderInfo('ที่อยู่', detail?.supplier?.address)}
            {renderInfo('เบอร์โทรศัพท์', detail?.supplier?.mobile_no)}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderClientInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดร้านค้า</BoxTitle>
        {renderInfo('ชื่อ', detail?.merchant?.name)}
        {renderInfo(
          'ที่อยู่',
          formatAddress(
            detail?.merchant?.address,
            detail?.merchant?.district,
            detail?.merchant?.amphor,
            detail?.merchant?.province,
            detail?.merchant?.postcode,
          ),
        )}
        {renderInfo('เบอร์โทรศัพท์', detail?.merchant?.phone)}
      </BoxWrapper>
    )
  }

  const renderCnInfo = () => {
    return (
      <BoxWrapper>
        <BoxTitle>รายละเอียดการลดหนี้</BoxTitle>
        <Row gutter={8}>
          <Col md={12} xs={24}>
            {renderInfo('เลขที่ใบลดหนี้', creditNotesData?.credit_note_no)}
            {renderInfo('วันที่ทำรายการลดหนี้', formatDateTime(creditNotesData?.created_at || ''))}
          </Col>
          <Col md={12} xs={24}>
            {renderInfo('ผู้ทำรายการลดหนี้', creditNotesData?.username)}
            {renderInfo('หมายเหตุ', creditNotesData?.remark)}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const renderImage = (product_photo: string) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + product_photo || ''} fallback={ImageFallback} />
  )

  const renderProductName = (name: string, value: PurchaseProduct) => {
    const isSameTitle = value?.product_name == value?.product_option_name
    return (
      <>
        <RowTile>{value?.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{value?.product_option_name}</ProductDesc>}
      </>
    )
  }

  const renderPrice = (item: number) => {
    return <TextRight>{numberWithComma(item || 0, true)}</TextRight>
  }

  const renderUom = (item: number, record: any) => {
    const data = detail?.items?.find(item => item.id === record.grn_item_id)

    const uom = (
      <ProductUomSystemInfo
        multiplier={data?.multiplier || 0}
        quantity={item}
        uom={data?.product_uom || ''}
        quantitySystem={item * (data?.multiplier || 0)}
        uomSystem={data?.product_uom_system || ''}
        showQuantity={true}
      />
    )
    return (
      <Space>
        {numberWithComma(item || 0, true)} {uom}
      </Space>
    )
  }

  const columns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return index + 1
      },
      width: '50px',
    },
    {
      title: <ColTile>รูปภาพ</ColTile>,
      dataIndex: 'product_image',
      key: 'product_image',
      render: renderImage,
      width: '100px',
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'product_sku',
      key: 'product_sku',
      width: '100px',
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'product_manufacture',
      key: 'product_manufacture',
      width: '100px',
      render: (value: PurchaseProduct) => value || '-',
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'product_name',
      key: 'product_name',
      render: renderProductName,
      width: '100px',
    },
    // {
    //   title: <ColTile>หน่วย</ColTile>,
    //   dataIndex: 'product_uom',
    //   key: 'product_uom',
    //   width: '100px',
    //   render: renderUom,
    // },
    {
      title: (
        <ColTile>
          <TextRight>จำนวนลดหนี้ (หน่วย)</TextRight>
        </ColTile>
      ),
      dataIndex: 'amount',
      key: 'amount',
      width: '100px',
      render: renderUom,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคาต่อหน่วย</TextRight>
        </ColTile>
      ),
      dataIndex: 'price_per_unit',
      key: 'price_per_unit',
      width: '100px',
      render: renderPrice,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคารวม</TextRight>
        </ColTile>
      ),
      dataIndex: 'price',
      key: 'price',
      width: '100px',
      render: (val: number) => <TextRight>{numberWithComma(val, true)}</TextRight>,
    },
  ]

  return (
    <DetailDrawer
      title={renderTitle()}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      width={'90%'}
      footer={null}
      maskClosable={true}
    >
      <Row justify="space-between" gutter={[16, 16]}>
        <Col span={24}>{renderCnInfo()}</Col>
        <Col md={24} xs={24}>
          {renderGrnInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderPurchaseInfo()}
        </Col>
        <Col md={12} xs={24}>
          {renderClientInfo()}
        </Col>
        <Col span={24}>
          <BoxWrapper>
            <Row justify="space-between">
              <Col>
                {' '}
                <BoxTitle>รายการลดหนี้</BoxTitle>
              </Col>
              <Col>
                <ReactToPrint
                  trigger={() => {
                    return (
                      <PrimaryButton
                        btnType="Approve"
                        text="ใบลดหนี้"
                        icon={<PrinterOutlined />}
                        onClick={() => {}}
                        size="middle"
                      />
                    )
                  }}
                  content={() => printPDF.current}
                  documentTitle={`ใบลดหนี้-${creditNotesData?.credit_note_no}`}
                />
              </Col>
              <Col span={24}>
                <TableListPage
                  rowKey="id"
                  tableName="product-purchase"
                  columns={columns}
                  datas={creditNotesData?.credit_note_items || []}
                  size={'small'}
                  scrollX={800}
                />
              </Col>
            </Row>
            <Row justify="space-between" gutter={[8, 8]}>
              <Col></Col>
              <Col>
                <SummaryPrice>
                  <Space>
                    จำนวนเงินรวมทั้งหมด
                    <Price> {numberWithComma(creditNotesData?.total_price, true)} </Price> บาท
                  </Space>
                </SummaryPrice>
              </Col>
            </Row>
          </BoxWrapper>
        </Col>

        <Col span={24} ref={printPDF}>
          <CnPDF detail={detail} creditNotesData={creditNotesData} />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ColTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const TextRight = styled.div`
  text-align: right;
`

const SummaryPrice = styled.div`
  text-align: right;
`

const Price = styled.div`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title2};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const mapStateToProps = () => {
  return {}
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(CnDetail)
